import { SagaIterator } from 'redux-saga';
import { getDeviationsByIds } from '@wix/da-shared-react/pkg/redux/deviations/selectors';
import { all, takeEvery, select, call, put } from 'redux-saga/effects';
import {
  BiData,
  GallectionSearchSubmitBiEvent,
  PostFeedViewBiEvent,
  ProfileNavBarBiEvent,
  PureBiData,
  ScrollToLoadMoreBiEvent,
} from '@wix/da-bi/pkg/events';
import { logPageView, biModalUnload } from '@wix/da-shared-react/pkg/biLogger/redux/actions';
import { logBiEvent } from '@wix/da-shared-react/pkg/biLogger/redux/saga';
import { ResourceType } from '@wix/da-shared-react/pkg/types/resource';
import isSSR from '@wix/da-shared-react/pkg/utils/isSSR';
import { getPagingMode } from '@wix/da-shared-react/pkg/publicSession/selectors';
import { appMounted } from '@wix/da-shared-react/pkg/redux/app/actions';
import { FETCH_SECTION_SUCCESS } from '../actions/sections';
import {
  getBiComponent,
  getPageViewBiData,
  getBiTypeAndItemId,
  getTypeAndItemIdOfProfileOwner,
} from '../selectors/biLogger';
import { biFetchMoreFolderItems, biClickProfileNavBar, biLogPostsFeedItems } from '../actions/bi';
import { selectors } from '@wix/da-shared-react/pkg/Stream';
import { exitDuperbrowseDone } from '@wix/da-shared-react/pkg/Duperbrowse/redux/actionCreators';
import { getSectionStreamId } from '../selectors/sections';

export function* logSearchSubmit(searchQuery: string) {
  if (isSSR()) {
    return; // clientside only, as this event is also fired on SSR when search query is set from URL's query string
  }
  const event = BiData<GallectionSearchSubmitBiEvent>({
    evid: 299,
    search_text: searchQuery,
  });
  yield call(logBiEvent, event);
}

export function* pageViewHandler() {
  if (isSSR()) {
    return; // clientside only
  }
  const pageViewData: ReturnType<typeof getPageViewBiData> = yield select(getPageViewBiData);
  yield put(logPageView(pageViewData));

  const streamId = yield select(getSectionStreamId);
  const entityIds = yield select(selectors.getItems, streamId);
  const deviations = yield select(getDeviationsByIds, entityIds);
  yield put(biLogPostsFeedItems(deviations));
}

function* fetchSectionHandler(action) {
  if (isSSR()) {
    return; // clientside only
  }
  const pageViewData: ReturnType<typeof getPageViewBiData> = yield select(getPageViewBiData);
  yield put(logPageView(pageViewData));
}

function* infiniteScrollHandler(action) {
  const extraData: ReturnType<typeof getBiTypeAndItemId> = yield select(getBiTypeAndItemId);
  const biTotalItemsCount = yield select(selectors.getTotalItemsCount);

  yield call(
    logBiEvent,
    PureBiData<ScrollToLoadMoreBiEvent>({
      evid: 98,
      itemid: extraData.itemid,
      typeid: extraData.typeid,
      offset: biTotalItemsCount,
    })
  );
}

function* biClickProfileNavBarHandler(action) {
  const { linkName } = action.payload;
  const eventComponent = yield select(getBiComponent);
  const { typeid, itemid } = yield select(getTypeAndItemIdOfProfileOwner);
  yield call(
    logBiEvent,
    BiData<ProfileNavBarBiEvent>({
      evid: 200,
      view: typeid === ResourceType.GROUP ? 'group' : 'profile',
      component: eventComponent,
      link_name: linkName,
      typeid,
      itemid,
    })
  );
}

export function* biLogPostsFeedItemsHandler(
  action: ReturnType<typeof biLogPostsFeedItems>
): SagaIterator {
  if (isSSR()) {
    return;
  }
  const {
    payload: { deviations },
  } = action;

  const streamId = yield select(getSectionStreamId);

  const pagingMode = yield select(getPagingMode);
  if (!streamId || !deviations || !deviations.length || pagingMode !== 'scroll') {
    // POST_FEED_VIEW event should only fire for infinite scroll
    return;
  }
  const streamItems = yield select(selectors.getItems, streamId);
  const offset = streamItems.length - deviations.length;
  const metadata = JSON.stringify(
    deviations.map((item, index) => ({
      typeid: item.typeId,
      item: item.deviationId,
      rank: offset + index,
    }))
  );
  const { itemid, typeid }: ReturnType<typeof getBiTypeAndItemId> =
    yield select(getBiTypeAndItemId);

  yield call(
    logBiEvent,
    PureBiData<PostFeedViewBiEvent>({
      evid: 95,
      itemid,
      typeid,
      offset,
      metadata,
    })
  );
}

export default function* biLogger() {
  yield all([
    takeEvery([appMounted, exitDuperbrowseDone, biModalUnload], pageViewHandler),
    takeEvery(FETCH_SECTION_SUCCESS, fetchSectionHandler),
    takeEvery(biFetchMoreFolderItems, infiniteScrollHandler),
    takeEvery(biClickProfileNavBar, biClickProfileNavBarHandler),
    takeEvery(biLogPostsFeedItems, biLogPostsFeedItemsHandler),
  ]);
}
